/**
 * PATH: packages/ui/src/kanban/components/card-detail/checklists.tsx
 * FILE_ID: UI-KANBAN-CMP-114
 * PURPOSE: Checklist section: tree build, item rows, drag reorder.
 * WHY: Split from the 4,176-line CardDetailDrawer monolith (UAT P5).
 *      Verbatim move - behaviour unchanged; the host re-exports the
 *      public surface so existing imports keep working.
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  Archive, Calendar, CheckSquare, ChevronUp, Clock, CornerDownRight, ExternalLink, FileText, Flag,
  Loader2, Mail, Merge, MessageSquare, Network, Paperclip, Pin, PinOff, Plus,
  RotateCcw,
  ShieldAlert, Tag as TagIcon, Trash2, UserPlus, X,
} from 'lucide-react'
import type { KanbanApi, KanbanEmailAdapter, KanbanMember } from '../../api'
import { KanbanCardConflictError } from '../../api'
import { MentionTextarea } from '../MentionTextarea'
import { PeoplePicker } from '../PeoplePicker'
import type {
  AuditLogEntry,
  Card, CardChecklistItemT, CardChecklistT, CardCommentT,
  CardStatus, Lane, Tag,
} from '../../types'
import { KanbanAvatar } from '../KanbanAvatar'
import { EmailLinkTickCross } from '../EmailLinkTickCross'
import { ChecklistItemContextMenu } from '../ChecklistItemContextMenu'
import {
  ChecklistItemRow,
  type ChecklistTreeNode,
} from './checklistItemRow'
import { SpeechMicButton } from '../SpeechMicButton'
import type { TrelloCardDetail } from '../../types'


// =============================================================================
// ChecklistsSection — multiple checklists per card with item toggles
// =============================================================================

export function ChecklistsSection({
  card,
  api,
  qc,
  boardId,
  hideTitle = false,
}: {
  card: Card
  api: KanbanApi
  qc: ReturnType<typeof useQueryClient>
  /** Audit P1-7 (Arc 3) — when present, invalidations stay scoped
   *  to this board's cards prefix. When absent, the legacy global
   *  ['kanban','cards'] invalidate runs as a back-compat shim. */
  boardId?: string
  /** ARC-4: suppress the inner h3 when a CollapsibleSection hosts this. */
  hideTitle?: boolean
}) {
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')

  const q = useQuery({
    queryKey: ['kanban', 'card', card.id, 'checklists'],
    queryFn: () => api.listChecklists(card.id),
  })

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id, 'checklists'] })
    // Activity reads card.audit_log_json from the full-card query; without
    // this, checklist create/delete leave Activity stale until reopen.
    qc.invalidateQueries({ queryKey: ['kanban', 'card', card.id] })
    // Audit P1-7 fix — was ['kanban','cards'] (matches every loaded
    // board's per-lane queries → 256 lane requests on Benko Desk per
    // checklist tick → 25.6s of wasted DB-CPU). Scoped to the
    // current board so other open boards stay quiet. The
    // non-board-scoped fallback keeps older host bundles working.
    if (boardId) {
      qc.invalidateQueries({ queryKey: ['kanban', 'cards', boardId] })
    } else {
      qc.invalidateQueries({ queryKey: ['kanban', 'cards'] })
    }
  }

  const addList = useMutation({
    mutationFn: (title: string) => api.createChecklist(card.id, title),
    onSuccess: () => {
      setNewName('')
      setAdding(false)
      invalidateAll()
    },
  })

  const deleteList = useMutation({
    mutationFn: (id: string) => api.deleteChecklist(id),
    onSuccess: invalidateAll,
  })

  return (
    <section>
      <div className={`mb-1 flex items-center ${hideTitle ? 'justify-end' : 'justify-between'}`}>
        {/* ARC-4: hideTitle when hosted inside a CollapsibleSection (which
            already renders the "Checklists" header + done/total count). */}
        {!hideTitle && (
          <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
            Checklists
          </h3>
        )}
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-200 dark:border-neutral-800 px-1.5 py-0.5 text-[11px] text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-900"
        >
          <CheckSquare className="h-3 w-3" /> Add checklist
        </button>
      </div>
      {adding && (
        <div className="mb-2 flex gap-1.5">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Checklist title…"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) addList.mutate(newName.trim())
              if (e.key === 'Escape') {
                setAdding(false)
                setNewName('')
              }
            }}
            className="flex-1 rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={() => newName.trim() && addList.mutate(newName.trim())}
            disabled={!newName.trim() || addList.isPending}
            className="rounded-md bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Add
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false)
              setNewName('')
            }}
            className="rounded-md px-2 py-1 text-[11px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
          >
            Cancel
          </button>
        </div>
      )}
      {q.isLoading && <p className="text-xs text-neutral-400">Loading…</p>}
      {q.data && q.data.length === 0 && !adding && (
        <p className="text-xs text-neutral-400">No checklists yet.</p>
      )}
      {q.data?.map((cl) => (
        <ChecklistBlock
          key={cl.id}
          checklist={cl}
          api={api}
          onChanged={invalidateAll}
          onDelete={() => deleteList.mutate(cl.id)}
        />
      ))}
    </section>
  )
}

/* ============================================================
 * Stack 12 MR-B — checklist-item tree rendering inside the drawer
 *
 * The block below replaces the previous flat `.map(...)` over
 * `checklist.items`. The server-side data shape is still a flat
 * list (`CardChecklistT.items` is unchanged), but we now read the
 * `parent` / `path` / `depth` fields Stack 12 MR-A added on each
 * item and compose the tree client-side via `buildChecklistTree`.
 *
 * UX affordances added in this MR-B:
 *   1. Recursive `<ChecklistItemRow>` indents one level per `depth`.
 *   2. Right-click on a row opens `ChecklistItemContextMenu` with
 *      Indent / Outdent / Make sub-of… / Promote to top / Delete.
 *   3. HTML5 drag — every row is both a drag source AND two drop
 *      zones: an inner "nest" zone (the row body) and an outer
 *      "reorder" zone (the top/bottom 4px strip). MIME
 *      `text/x-checklist-item-id`.
 *   4. Multi-select via Shift-click or checkbox modifier — turns the
 *      "Make sub-item of…" submenu into "Nest selected items here".
 *
 * Server endpoints (qb!2962 — Stack 12 MR-B BE):
 *   POST /api/kanban/checklist-items/<id>/move/    {parent_id, position}
 *   POST /api/kanban/checklist-items/<id>/detach/
 * ============================================================ */

/**
 * Compose a tree from the flat server list. Top-level items are
 * those whose `parent` is null; everything else is grouped under
 * its parent. Within each parent, children are ordered by
 * `position` (server-side write contract).
 *
 * Defensive: items whose `parent` doesn't match any known item id
 * (shouldn't happen — would imply backend bug) are surfaced at top
 * level so the operator can still see + edit them.
 */
function buildChecklistTree(items: CardChecklistItemT[]): ChecklistTreeNode[] {
  const byId = new Map<string, ChecklistTreeNode>()
  for (const i of items) byId.set(i.id, { item: i, children: [] })
  const roots: ChecklistTreeNode[] = []
  for (const node of byId.values()) {
    const p = node.item.parent
    if (p === null || p === undefined || !byId.has(p)) {
      roots.push(node)
    } else {
      byId.get(p)!.children.push(node)
    }
  }
  const sortByPos = (nodes: ChecklistTreeNode[]) => {
    nodes.sort((a, b) => a.item.position - b.item.position)
    for (const n of nodes) sortByPos(n.children)
  }
  sortByPos(roots)
  return roots
}

function ChecklistBlock({
  checklist,
  api,
  onChanged,
  onDelete,
}: {
  checklist: CardChecklistT
  api: KanbanApi
  onChanged: () => void
  onDelete: () => void
}) {
  // Operator complaint 2026-05-19: "just clicking inside the
  // checklist I should be able to start typing enter and create the
  // next item". The input is always rendered (Trello-style) and
  // re-focuses after each submit so the operator can rattle off a
  // list without touching the mouse.
  const [newItem, setNewItem] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const total = checklist.items.length
  const done = checklist.items.filter((i) => i.complete).length
  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  // Stack 12 MR-B: tree + multi-select + context menu state.
  const tree = useMemo(() => buildChecklistTree(checklist.items), [checklist.items])
  const itemById = useMemo(() => {
    const m = new Map<string, CardChecklistItemT>()
    for (const i of checklist.items) m.set(i.id, i)
    return m
  }, [checklist.items])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [menu, setMenu] = useState<{
    item: CardChecklistItemT
    anchor: { x: number; y: number }
  } | null>(null)

  // Reset selection if the underlying item set changes meaningfully
  // (e.g. parent invalidated after a mutation removed a selected id).
  useEffect(() => {
    if (selectedIds.size === 0) return
    let mutated = false
    const next = new Set<string>()
    for (const id of selectedIds) {
      if (itemById.has(id)) next.add(id)
      else mutated = true
    }
    if (mutated) setSelectedIds(next)
  }, [itemById, selectedIds])

  const addItem = useMutation({
    mutationFn: (title: string) =>
      api.createChecklistItem({ checklist: checklist.id, title }),
    onSuccess: () => {
      setNewItem('')
      onChanged()
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    },
  })

  const moveMutation = useMutation({
    mutationFn: (vars: { id: string; parent_id: string | null }) =>
      api.moveChecklistItem(vars.id, { parent_id: vars.parent_id }),
    onSuccess: onChanged,
  })

  const detachMutation = useMutation({
    mutationFn: (id: string) => api.detachChecklistItem(id),
    onSuccess: onChanged,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteChecklistItem(id),
    onSuccess: onChanged,
  })

  // STACK13-MRD — promote a checklist item into a sub-card under
  // the containing card, in the same lane. Hidden when the host
  // didn't wire `api.promoteChecklistItemToCard` (read-only embeds).
  const promoteToCardMutation = useMutation({
    mutationFn: (id: string) => {
      if (!api.promoteChecklistItemToCard) {
        throw new Error('promoteChecklistItemToCard is not wired by this host')
      }
      return api.promoteChecklistItemToCard(id)
    },
    onSuccess: onChanged,
  })

  // Multi-select handler — plain click toggles, Shift-click extends
  // from the most-recently selected to the clicked item (server
  // order). Empty shift-range falls back to plain toggle.
  const lastSelectedRef = useRef<string | null>(null)
  function handleToggleSelect(id: string, shiftKey: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (shiftKey && lastSelectedRef.current && lastSelectedRef.current !== id) {
        const order = checklist.items
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((i) => i.id)
        const start = order.indexOf(lastSelectedRef.current!)
        const end = order.indexOf(id)
        if (start >= 0 && end >= 0) {
          const [lo, hi] = start < end ? [start, end] : [end, start]
          for (let k = lo; k <= hi; k += 1) next.add(order[k])
          lastSelectedRef.current = id
          return next
        }
      }
      if (next.has(id)) next.delete(id)
      else next.add(id)
      lastSelectedRef.current = id
      return next
    })
  }

  function handleContextMenu(
    item: CardChecklistItemT,
    ev: React.MouseEvent,
  ) {
    setMenu({ item, anchor: { x: ev.clientX, y: ev.clientY } })
  }

  // The Indent shortcut nests `item` under the previous sibling at
  // the same depth in server order. Returns null if no eligible
  // sibling exists.
  function findPreviousSiblingId(item: CardChecklistItemT): string | null {
    const sameDepth = checklist.items
      .filter((s) => s.parent === item.parent && s.id !== item.id)
      .sort((a, b) => a.position - b.position)
    let prev: CardChecklistItemT | null = null
    for (const s of sameDepth) {
      if (s.position < item.position) prev = s
      else break
    }
    return prev?.id ?? null
  }

  // Outdent moves `item` from its current parent up to its
  // grandparent. If the grandparent is null we detach to top-level.
  function findGrandparentId(item: CardChecklistItemT): string | null {
    if (item.parent === null) return null
    const parent = itemById.get(item.parent)
    return parent?.parent ?? null
  }

  return (
    <div className="mb-2 rounded-md border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 p-2">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <CheckSquare className="h-3 w-3 text-neutral-500 shrink-0" />
          <span className="truncate text-xs font-semibold text-neutral-800 dark:text-neutral-100">
            {checklist.title}
          </span>
          {selectedIds.size > 0 && (
            <span
              className="ml-1 rounded-full bg-blue-100 px-1.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
              title="Multi-select active — right-click another item to nest these under it."
            >
              {selectedIds.size} selected
            </span>
          )}
        </div>
        {selectedIds.size > 0 && (
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="rounded px-1 py-0.5 text-[10px] text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-900"
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (confirm(`Delete checklist "${checklist.title}"?`)) onDelete()
          }}
          aria-label="Delete checklist"
          className="rounded p-0.5 text-neutral-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      {total > 0 && (
        <div className="mb-2 flex items-center gap-2 text-[10px] text-neutral-500">
          <span className="font-mono w-8 text-right">{pct}%</span>
          <div className="flex-1 h-1 rounded bg-neutral-200 dark:bg-neutral-800 overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="font-mono w-10">{done}/{total}</span>
        </div>
      )}
      <ul className="space-y-0.5">
        {tree.map((node) => (
          <ChecklistItemRow
            key={node.item.id}
            node={node}
            api={api}
            siblings={checklist.items}
            onChanged={onChanged}
            onContextMenu={handleContextMenu}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
          />
        ))}
      </ul>
      {/* Always-rendered Trello-style inline-add. */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          const next = newItem.trim()
          if (!next || addItem.isPending) return
          addItem.mutate(next)
        }}
        className="mt-1 flex items-center gap-1"
      >
        <Plus className="h-3 w-3 shrink-0 text-neutral-400" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="Add an item — Enter to save"
          disabled={addItem.isPending}
          className="min-w-0 flex-1 rounded bg-transparent px-1 py-0.5 text-xs text-neutral-700 placeholder:text-neutral-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500/30 disabled:opacity-60 dark:text-neutral-200 dark:focus:bg-neutral-900"
        />
      </form>

      {menu && (
        <ChecklistItemContextMenu
          item={menu.item}
          siblings={checklist.items}
          anchor={menu.anchor}
          multiSelectCount={selectedIds.size}
          multiSelectIds={selectedIds}
          onIndent={() => {
            const prev = findPreviousSiblingId(menu.item)
            if (!prev) return
            moveMutation.mutate({ id: menu.item.id, parent_id: prev })
          }}
          onOutdent={() => {
            const gp = findGrandparentId(menu.item)
            moveMutation.mutate({ id: menu.item.id, parent_id: gp })
          }}
          onNestUnder={(parentId) =>
            moveMutation.mutate({ id: menu.item.id, parent_id: parentId })
          }
          onPromoteToTop={() => detachMutation.mutate(menu.item.id)}
          onPromoteToCard={
            api.promoteChecklistItemToCard
              ? () => promoteToCardMutation.mutate(menu.item.id)
              : undefined
          }
          onNestSelectedHere={(parentId) => {
            // Fire all moves; React-Query invalidate at the end picks
            // up the resulting tree. The server enforces the
            // cross-checklist + cycle invariants, so a race that
            // would create a cycle 400s harmlessly.
            for (const id of selectedIds) {
              moveMutation.mutate({ id, parent_id: parentId })
            }
            setSelectedIds(new Set())
          }}
          onDelete={() => deleteMutation.mutate(menu.item.id)}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  )
}
